import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Moon, Sun, Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import './Navigation.css';
import { trackEvent } from './analytics';

const LANGUAGES = [
    { code: 'en', label: 'English', short: 'EN' },
    { code: 'fr', label: 'Français', short: 'FR' },
    { code: 'ar', label: 'العربية', short: 'AR' },
];

export const Logo = ({ subtitle }) => (
    <a href="#Home" className="brand-logo" aria-label="Youssef Ben Henda">
        <span className="brand-mark">YB</span>
        <span className="brand-name">
            {subtitle ? (
                <>
                    <strong>Youssef Ben Henda</strong>
                    <small>{subtitle}</small>
                </>
            ) : (
                <>
                    <strong>Youssef</strong>
                    <strong>Ben Henda</strong>
                </>
            )}
        </span>
    </a>
);

const Navigation = ({ darkMode, setDarkMode, links }) => {
    const { t, i18n } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [active, setActive] = useState('#Home');
    const langRef = useRef(null);
    const linksRef = useRef(null);
    const indicatorRef = useRef(null);
    const indicatorPlaced = useRef(false);

    const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

    const applyDir = (code) => {
        document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = code;
    };

    const switchLang = (code) => {
        if (code !== i18n.language) trackEvent('Language Change', { language: code });
        i18n.changeLanguage(code);
        try { localStorage.setItem('lang', code); } catch { /* storage unavailable */ }
        applyDir(code);
        setLangOpen(false);
        setIsMenuOpen(false);
    };

    useEffect(() => { applyDir(i18n.language); }, [i18n.language]);

    useEffect(() => {
        const onScroll = () => {
            setScrolled(window.scrollY > 8);
            // The last section (Contact) is too short to reach the middle of the viewport
            const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
            if (atBottom) setActive(links[links.length - 1].href);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [links]);

    // Highlight the nav link of the section currently in view
    useEffect(() => {
        const sections = links
            .map(({ href }) => document.querySelector(href))
            .filter(Boolean);
        if (!sections.length || !('IntersectionObserver' in window)) return undefined;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) setActive(`#${entry.target.id}`);
            });
        }, { rootMargin: '-45% 0px -50% 0px' });
        sections.forEach(s => observer.observe(s));
        return () => observer.disconnect();
    }, [links]);

    // Sliding active-section indicator (desktop): follows the active link's underline position
    useLayoutEffect(() => {
        const container = linksRef.current;
        const indicator = indicatorRef.current;
        if (!container || !indicator) return undefined;

        const place = () => {
            const link = container.querySelector('.nav-link.active');
            if (!link || !link.offsetWidth) {
                container.classList.remove('has-indicator');
                return;
            }
            const x = link.offsetLeft + link.offsetWidth / 2 - indicator.offsetWidth / 2;
            const y = link.offsetTop + link.offsetHeight - indicator.offsetHeight;
            // First placement (and re-layouts) jump straight there; section changes glide
            indicator.style.transition = indicatorPlaced.current ? '' : 'none';
            indicator.style.transform = `translate3d(${x}px, ${y}px, 0)`;
            container.classList.add('has-indicator');
            indicatorPlaced.current = true;
        };

        place();
        const onResize = () => { indicatorPlaced.current = false; place(); };
        let observer;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(onResize);
            observer.observe(container);
        }
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(onResize).catch(() => {});
        return () => observer && observer.disconnect();
    }, [active, i18n.language, links]);

    useEffect(() => {
        const handler = (e) => {
            if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <header className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${isMenuOpen ? 'navbar--open' : ''}`}>
            <nav className="navbar-inner container">
                <Logo />

                <div className="nav-links" ref={linksRef}>
                    <span className="nav-indicator" ref={indicatorRef} aria-hidden="true" />
                    {links.map(({ key, href }) => (
                        <a
                            key={key}
                            href={href}
                            className={`nav-link ${active === href ? 'active' : ''}`}
                            onClick={() => setIsMenuOpen(false)}
                        >
                            {t(`nav.${key}`)}
                        </a>
                    ))}
                </div>

                <div className="nav-actions">
                    <div className="lang-switcher" ref={langRef}>
                        <button
                            className="lang-btn"
                            onClick={() => setLangOpen(v => !v)}
                            aria-label={t('nav.language')}
                            aria-expanded={langOpen}
                        >
                            {currentLang.short}
                            <ChevronDown size={14} className={`lang-chevron ${langOpen ? 'open' : ''}`} />
                        </button>
                        {langOpen && (
                            <div className="lang-dropdown">
                                {LANGUAGES.map(lang => (
                                    <button
                                        key={lang.code}
                                        className={`lang-option ${lang.code === i18n.language ? 'active' : ''}`}
                                        onClick={() => switchLang(lang.code)}
                                    >
                                        <span className="lang-short">{lang.short}</span>
                                        {lang.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="theme-toggle" role="group" aria-label={t('nav.theme')}>
                        <button
                            className={`icon-btn ${darkMode ? 'icon-btn--outlined' : ''}`}
                            onClick={() => {
                                if (darkMode) trackEvent('Theme Change', { theme: 'light' });
                                setDarkMode(false);
                            }}
                            aria-pressed={!darkMode}
                            aria-label={t('nav.light')}
                        >
                            <Sun size={18} />
                        </button>
                        <button
                            className={`icon-btn ${darkMode ? '' : 'icon-btn--outlined'}`}
                            onClick={() => {
                                if (!darkMode) trackEvent('Theme Change', { theme: 'dark' });
                                setDarkMode(true);
                            }}
                            aria-pressed={darkMode}
                            aria-label={t('nav.dark')}
                        >
                            <Moon size={18} />
                        </button>
                    </div>

                    <a href="#Contact" className="btn btn-primary btn-xs nav-cta" onClick={() => trackEvent('CTA Click', { button: 'lets_work' })}>
                        {t('nav.lets_work')}
                        <ArrowRight size={16} className="flip-rtl" />
                    </a>

                    <button
                        className="icon-btn icon-btn--outlined hamburger-btn"
                        onClick={() => setIsMenuOpen(v => !v)}
                        aria-label={t('nav.menu')}
                        aria-expanded={isMenuOpen}
                    >
                        {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </nav>
        </header>
    );
};

export default Navigation;
